import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './PdfViewer.css';

function PdfViewer() {
  const { pdfId, userId } = useParams();
  const navigate = useNavigate();
  const [timer, setTimer] = useState<number>(60);
  const [pdfData, setPdfData] = useState<any>(null);
  const [marked, setMarked] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(true);
  const [hasWindowFocus, setHasWindowFocus] = useState(true);

  // Überwache Tab-Sichtbarkeit UND Window-Fokus (wichtig für Mobile)
  useEffect(() => {
    const updateTimerState = () => {
      const visible = !document.hidden;
      const focused = document.hasFocus();
      console.log('Timer state update - visible:', visible, 'focused:', focused);
      setIsTabVisible(visible);
      setHasWindowFocus(focused);
    };

    const handleVisibilityChange = () => {
      console.log('Visibility changed, hidden:', document.hidden);
      updateTimerState();
    };

    const handleFocus = () => {
      console.log('Window focused');
      updateTimerState();
    };

    const handleBlur = () => {
      console.log('Window blurred');
      setHasWindowFocus(false);
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      console.log('Page show event, from cache:', event.persisted);
      // Nur States setzen wenn Seite wirklich sichtbar ist
      if (!document.hidden) {
        updateTimerState();
      }
    };

    const handlePageHide = () => {
      console.log('Page hide event');
      setHasWindowFocus(false);
      setIsTabVisible(false);
    };

    // Initial state setzen
    updateTimerState();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pageshow', handlePageShow as EventListener);
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pageshow', handlePageShow as EventListener);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  useEffect(() => {
    // Prüfe ob Mobile-Gerät
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
    };
    checkMobile();
  }, []);

  useEffect(() => {
    // Lade PDF-Daten
    const loadPdf = async () => {
      try {
        const res = await fetch(`/api/pdf/${pdfId}`);
        const data = await res.json();
        setPdfData(data);
        
        // Auf Mobile: Öffne PDF direkt im nativen Viewer und markiere als gelesen
        if (isMobile) {
          window.location.href = `/uploads/${data.filename}`;
          // Markiere direkt als gelesen für mobile Geräte
          markAsRead();
        }
      } catch (error) {
        console.error('Fehler beim Laden der PDF:', error);
      }
    };
    if (isMobile !== null) {
      loadPdf();
    }
  }, [pdfId, isMobile]);

  // Timer - nur auf Desktop aktiv
  useEffect(() => {
    // Auf Mobile kein Timer, auf Desktop nur wenn Tab sichtbar und fokussiert
    if (isMobile) return; // Kein Timer auf Mobile
    
    const isActive = isTabVisible && hasWindowFocus;
    console.log('Timer effect - isMobile:', isMobile, 'isActive:', isActive, 'timer:', timer, 'marked:', marked);
    
    if (timer === 0 || marked || !isActive) return;

    const interval = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          markAsRead();
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timer, marked, isTabVisible, hasWindowFocus, isMobile]);

  const markAsRead = async () => {
    if (marked) return;
    try {
      await fetch(`/api/user/${userId}/mark-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pdf_id: pdfId })
      });
      setMarked(true);
    } catch (error) {
      console.error('Fehler beim Markieren:', error);
    }
  };

  if (!pdfData) {
    return <div className="pdf-viewer-page"><p>Lade PDF...</p></div>;
  }

  return (
    <div className="pdf-viewer-page">
      <div className="pdf-header">
        <img src="/logo.png" alt="Logo" className="pdf-logo" />
        <h2>{pdfData.filename.replace(/^\d+-/, '').replace(/_/g, ' ').replace('.pdf', '')}</h2>
        <div className="pdf-timer">
          {isMobile ? (
            <span style={{color: 'green', fontWeight: 'bold'}}>✓ PDF als gelesen markiert!</span>
          ) : (
            <>
              {timer > 0 ? (
                <>
                  <span>⏱️ Verbleibende Zeit: <strong>{timer}</strong> Sekunden</span>
                  {(!isTabVisible || !hasWindowFocus) && (
                    <div style={{fontSize: '0.9rem', marginTop: '5px', color: '#ff6b6b', fontWeight: 'bold'}}>
                      ⏸️ Timer pausiert - Bitte zur PDF zurückkehren
                    </div>
                  )}
                </>
              ) : (
                <span style={{color: 'green', fontWeight: 'bold'}}>✓ PDF als gelesen markiert!</span>
              )}
            </>
          )}
        </div>
      </div>
      {isMobile ? (
        <div className="pdf-content" style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px'}}>
          <p style={{fontSize: '1.1rem', textAlign: 'center', marginBottom: '20px', color: '#666'}}>
            Die PDF wird in der nativen PDF-Ansicht Ihres Geräts geöffnet.
          </p>
          <a 
            href={`/uploads/${pdfData.filename}`} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              background: '#FF8C00',
              color: 'white',
              padding: '15px 30px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '1.1rem',
              fontWeight: 'bold'
            }}
          >
            📄 PDF öffnen
          </a>
          <p style={{fontSize: '0.9rem', textAlign: 'center', marginTop: '30px', color: '#999'}}>
            Das Dokument wurde bereits als gelesen markiert. Bitte lesen Sie die PDF vollständig durch.
          </p>
        </div>
      ) : (
        <div className="pdf-content">
          <iframe 
            src={`/uploads/${pdfData.filename}`} 
            width="100%" 
            height="100%" 
            title="PDF"
            style={{border: 'none'}}
          />
        </div>
      )}
      {(timer === 0 || isMobile) && (
        <div className="pdf-footer">
          <button onClick={() => window.close()}>Tab schließen</button>
        </div>
      )}
    </div>
  );
}

export default PdfViewer;
